import { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { IconFile, IconFileTypePdf, IconMusic } from "@tabler/icons-react";
import env from "@configs/env";

function resolveMediaUrl(rawUrl) {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://"))
    return rawUrl;
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/api\/?$/,
    "",
  );
  return `${base}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
}

function MediaUpdate({ media, onUpdate, onCancel, submitting }) {
  const mediaId = media?.mmd_id || media?.id;
  const rawUrl = media?.mmd_url || media?.url || "";
  const mediaUrl = resolveMediaUrl(rawUrl);
  const mediaType = (
    media?.mmd_media_type ||
    media?.mediaType ||
    ""
  ).toLowerCase();
  const mimeType = (
    media?.mmd_mime_type ||
    media?.mimeType ||
    ""
  ).toLowerCase();
  const [file, setFile] = useState(null);

  const onDrop = useMemo(
    () => (acceptedFiles) => {
      if (!acceptedFiles?.length) return;
      setFile(acceptedFiles[0]);
    },
    [],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  async function handleSubmit(event) {
    event.preventDefault();
    if (!mediaId || !file) return;
    await onUpdate(String(mediaId), { file });
  }

  if (!mediaId) {
    return (
      <div className="form-grid">
        <p className="message-error">The resource to edit was not found.</p>
        <button className="button-ghost" type="button" onClick={onCancel}>
          Back
        </button>
      </div>
    );
  }

  return (
    <form className="form-grid" onSubmit={handleSubmit}>
      <div className="media-current">
        <p className="field-label">Current file</p>
        {mediaType === "image" || mimeType.startsWith("image/") ? (
          <img
            src={mediaUrl}
            alt="Current file"
            className="media-current-thumb"
          />
        ) : null}
        {mediaType === "video" || mimeType.startsWith("video/") ? (
          <video className="media-current-thumb" controls preload="metadata">
            <source src={mediaUrl} type={mimeType || undefined} />
          </video>
        ) : null}
        {mediaType === "audio" || mimeType.startsWith("audio/") ? (
          <div className="media-current-file">
            <IconMusic size={18} />
            <a href={mediaUrl} target="_blank" rel="noreferrer">
              Open current audio
            </a>
          </div>
        ) : null}
        {mediaType !== "image" &&
        mediaType !== "video" &&
        mediaType !== "audio" &&
        !mimeType.startsWith("image/") &&
        !mimeType.startsWith("video/") &&
        !mimeType.startsWith("audio/") ? (
          <div className="media-current-file">
            {mimeType.includes("pdf") ? (
              <IconFileTypePdf size={18} />
            ) : (
              <IconFile size={18} />
            )}
            <a href={mediaUrl} target="_blank" rel="noreferrer">
              Open current file
            </a>
          </div>
        ) : null}
      </div>

      <p className="field-label">
        Replace the file. The backend automatically updates type, mime, size,
        URL, storage provider, and status.
      </p>
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? "dropzone-active" : ""} ${file ? "dropzone-has-file" : ""}`}
      >
        <input {...getInputProps()} />
        <p>
          {isDragActive
            ? "Drop the file here..."
            : "Drag or click to replace the file."}
        </p>
        {file ? (
          <small>{file.name}</small>
        ) : (
          <small>You must select a file to update.</small>
        )}
      </div>

      <button
        className="button-primary"
        type="submit"
        disabled={submitting || !file}
      >
        {submitting ? "Updating..." : "Update media"}
      </button>
    </form>
  );
}

export default MediaUpdate;
